/**
 * Supabase Storage image transform helper.
 *
 * Why this exists:
 *   Lighthouse "Optimer billedlevering" flagged ~1.9 MiB of waste from CarCard
 *   thumbnails: source PNGs/JPGs are 1420×1066 (~250–470 KiB each) but the
 *   card only displays them at 717×478. The fix is to ask Supabase Storage
 *   to resize+re-encode on the fly via its `/render/image/public/...` route
 *   instead of serving the raw `/storage/v1/object/public/...` original.
 *
 * Output: roughly 30–50 KiB per WebP thumbnail at 800w / quality 75 — a
 * ~6–10× reduction vs. the original PNG/JPG.
 *
 * Behaviour:
 *   - Only rewrites Supabase Storage public-object URLs. Other URLs
 *     (Bilinfo CDN, absolute remote photos, local assets) are returned
 *     untouched so the helper is safe to wrap any image src.
 *   - Preserves the legacy `thumbs/<file>.webp` convention used by older
 *     uploads — those already-shrunk thumbnails are returned as-is so we
 *     don't double-transform.
 *   - Drops any pre-existing query string. The render endpoint owns the
 *     query namespace (`width`, `height`, `quality`, `resize`, `format`).
 */

const PUBLIC_OBJECT_PREFIX = "/storage/v1/object/public/";
const RENDER_IMAGE_PREFIX = "/storage/v1/render/image/public/";

export interface SupabaseImageTransformOptions {
  /** Target render width in CSS pixels. Supabase will downscale only — never upscale. */
  width: number;
  /** Optional explicit height; omit to preserve aspect ratio. */
  height?: number;
  /** JPEG/WebP quality 20–100. Default 75 — visually indistinguishable for car thumbnails. */
  quality?: number;
  /**
   * "contain" (default — proportional downscale within the width box, keeps
   * aspect ratio), "cover" (force-fill the bounding box), or "fill" (stretch).
   *
   * IMPORTANT: Supabase maps `cover` to its underlying `resizing_type:fill`,
   * which — when only `width` is supplied — keeps the ORIGINAL height and
   * just scales the width. For a 1600×1066 source asked at width=400 that
   * produces a 400×1066 narrow strip; `<img object-cover>` then shows only
   * the middle slice, which looks like the photo is "zoomed in". Using
   * `contain` preserves aspect ratio and lets the card's own `aspect-[4/3]`
   * + `object-cover` do the framing the way the design intends.
   */
  resize?: "cover" | "contain" | "fill";
  /**
   * Output format. Defaults to "origin" which lets Supabase pick based on the
   * `Accept` header (delivers WebP to modern browsers, falls back to the
   * original format otherwise — no JPG-to-PNG bloat).
   */
  format?: "origin" | "webp";
}

export function supabaseImageUrl(rawUrl: string, opts: SupabaseImageTransformOptions): string {
  if (!rawUrl) return rawUrl;

  // Already a hand-built `thumbs/foo.webp` — these were generated at upload
  // time and are already small. Re-running them through the transform would
  // waste CPU AND defeat the cache (the transform endpoint has its own CDN
  // hit/miss table separate from raw object storage).
  if (/\/storage\/v1\/object\/public\/car-images\/thumbs\//.test(rawUrl)) {
    return rawUrl;
  }

  // Only rewrite public Supabase object URLs — leave everything else alone.
  const idx = rawUrl.indexOf(PUBLIC_OBJECT_PREFIX);
  if (idx === -1) return rawUrl;

  // Strip the original query string; the transform endpoint owns query params.
  const pathPart = rawUrl.slice(0, idx) + RENDER_IMAGE_PREFIX + rawUrl.slice(idx + PUBLIC_OBJECT_PREFIX.length).split("?")[0];

  const params = new URLSearchParams();
  params.set("width", String(Math.round(opts.width)));
  if (opts.height) params.set("height", String(Math.round(opts.height)));
  params.set("quality", String(opts.quality ?? 75));
  params.set("resize", opts.resize ?? "contain");
  // Default to `origin` — Supabase content-negotiates WebP to capable
  // browsers and serves the original format to legacy ones, which avoids
  // the "WebP to a Safari that wanted JPEG" caching gotcha.
  if (opts.format && opts.format !== "origin") params.set("format", opts.format);

  return `${pathPart}?${params.toString()}`;
}

/**
 * Build a `srcset` string for two pixel-density variants (1x + 2x) of a
 * Supabase-hosted thumbnail. Use with `<img sizes="...">` so retina screens
 * still get a crisp image without forcing 1x devices to download 2x bytes.
 */
export function supabaseImageSrcSet(rawUrl: string, baseWidth: number, opts?: Omit<SupabaseImageTransformOptions, "width">): string {
  if (!rawUrl) return "";
  const idx = rawUrl.indexOf(PUBLIC_OBJECT_PREFIX);
  if (idx === -1) return ""; // Non-Supabase URL — no srcset, browser falls back to `src`.
  const x1 = supabaseImageUrl(rawUrl, { ...opts, width: baseWidth });
  const x2 = supabaseImageUrl(rawUrl, { ...opts, width: baseWidth * 2 });
  return `${x1} 1x, ${x2} 2x`;
}
